import type { TokenDenom } from '@/hooks/useTokenPrices';
import type { Models, Provider } from '@/types/maketPlace';

import type { CollectionData } from '../collection';
import type { Prompt } from '../prompt';
import type { IUsecaseDetail } from '../usecase';

export interface ResAssets {
  items: Assets[];
  total: number;
}

export enum STATUS_POST {
  ACTIVE = 0,
  DELETE = 1,
}

export interface Assets {
  id: number;
  name: string;
  description: string;
  hash: string;
  originalHash: any;
  picture: string;
  pictureMimeType: string;
  url: string;
  mimeType: string;
  color: any;
  status: number;
  autoGeneratingPost: number;
  nftStatus: number;
  blockTime: string;
  blockHash: any;
  txHash: string;
  accountAddress: string;
  duration: any;
  createdBy: number;
  creator: number;
  updatedBy: number;
  modelId: number;
  collectionId: number;
  totalQuantity: number;
  availableQuantity: number;
  numberOfOwners: number;
  numberOfReactions: number;
  tokenContract: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  generatorRoyalty: number;
  provider: Provider;
  model: Models;
  collection: Collection;
  offer: Offer;
  creatorProvider: CreatorProvider;
  attributes: IAttributes;
  isNsfw?: boolean;
}

export interface Collection {
  id: number;
  name: string;
  description: string;
  picture: string;
  pictureMimeType: string;
  thumbnail: string;
  thumbnailMimeType: string;
  cover: string;
  miniCover: string;
  status: number;
  links: Link[];
  createdBy: number;
  accountAddress: string;
  numberOfItems: number;
  numberOfOwners: number;
  tradingVolume: number;
  tradingVolumeOrai: number;
  createdAt: string;
  updatedAt: string;
}

export interface Link {
  type: string;
  url: string;
}

export interface Offer {
  id: number;
  tokenId: number;
  offeringId: number;
  auctionId: number | null;
  sellerAddress: string | null;
  creatorAddress: string | null;
  bidderAddress: string | null;
  amount: number;
  type: number;
  status: number;
  openPrice: number | null;
  buyoutPrice: number | null;
  currentPrice: number | null;
  lowestPrice: LowestPrice[];
  highestPrice: HighestPrice[];
  auctionStepAmount: number | null;
  creatorRoyalty: number | null;
  sellerRoyalty: number;
  previousOwnerAddress: string | null;
  previousOwnerRoyalty: number;
  offerAt: string | null;
  closeAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  contractAddress: string | null;
  denom: string;
}

export interface LowestPrice {
  minPrice: number;
  denom: string;
}

export interface HighestPrice {
  maxPrice: number;
  denom: string;
}

export interface CreatorProvider {
  id: number;
  username: string;
  picture: string;
  cover: string;
  status: number;
  publicAddress: string;
  description: string;
  job: any;
  tags: any;
  links: Link2[];
  createdBy: any;
  updatedBy: number;
  role: number;
  totalNfts: number;
  isEmailVerified: boolean;
  isDiscordVerified: boolean;
  stripeId: any;
  createdAt: string;
  updatedAt: string;
  newNotificationsCount: number;
  nonce: string;
}

export interface Link2 {
  type: string;
  url: string;
}

export interface AssetPost {
  status: STATUS_POST;
  id: number;
  assetId: number;
  totalComments: number;
  totalReactions: number;
  text: string | null;
  event: string;
  createdBy: number;
  createdAt: string;
}

export interface ITrustWorthyAsset {
  assetId: number;
  chainId: string;
  contract: string;
  createdAt: string;
  id: number;
  metadataUrl: string;
  txHash: string;
  type: string;
}

export interface IAssetDetail {
  pAssetId: number;
  modelConfig: string;
  totalDownload: number;
  collection: CollectionData;
  id: number;
  name: string;
  description: string;
  hash: string;
  campaign: any;
  originalHash: string;
  picture: string;
  pictureMimeType: string;
  url: string;
  mimeType: string;
  color: string;
  status: number;
  autoGeneratingPost: number;
  nftStatus: number;
  blockTime: string;
  blockHash: string;
  txHash: string;
  accountAddress: string;
  duration: string;
  createdBy: number;
  creator: IUser | null;
  updatedBy: number;
  modelId: number;
  collectionId: number;
  totalQuantity: number;
  availableQuantity: number;
  numberOfOwners: number;
  numberOfReactions: number;
  tokenContract: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  generatorRoyalty: number;
  provider: IUser;
  postId: number;
  post: AssetPost;
  model: IModel;
  offer: IOffer | null;
  creators: ICreators;
  creatorProvider: IUser | null;
  mixingOffer: IOffer | null;
  ownerStatus?: IOwnerStatus;
  tags: ITag[];
  attributes: IAttributes;
  isPlus: boolean;
  isNsfw?: boolean;
  prompt: null | Prompt;
  promptId: null | number;
  totalView: number;
  tickets: any;
  promptDetails: {
    asset: IAssetDetail;
    avatar: string;
    createdAt: string;
    creator: number;
    displayPrompt: string;
    hash: string;
    id: number;
    message: null | string;
    mimeType: string;
    name: null | string;
    numberOfReactions: number;
    numberOfWords: number;
    status: number;
    tokenId: number;
    updatedAt: string;
    prompt: string;
  };
}

export interface IUser {
  sVerify: string;
  id: number;
  username: string;
  picture: string;
  cover: string;
  status: number;
  publicAddress: string;
  description: string;
  job: string | null;
  tags: string[] | null;
  links: ILink[] | null;
  createdBy: number | null;
  updatedBy: number;
  role: number;
  totalNfts: number;
  isEmailVerified: boolean;
  isDiscordVerified: boolean;
  stripeId: string | null;
  createdAt: string;
  updatedAt: string;
  newNotificationsCount: number;
  nonce: string;
}

export interface IModel {
  id: number;
  name: string;
  description: string;
  thumbnail: string;
  thumbnailMimeType: string;
  picture: string;
  cover: string | null;
  apiSignature: string;
  apiContentType: string;
  status: number;
  isHeavyModel: number;
  isHeavySimilarity: number;
  providerId: number;
  updatedBy: number;
  categoryId: number;
  totalUsedTimes: number | null;
  threshold: number | null;
  createdAt: string;
  updatedAt: string;
  properties: IProperties;
  creatorAddress: string;
}

export interface IProperties {
  schema: ISchema[] | null;
}

export interface ISchema {
  key: string;
  type: string;
  name: string;
}

interface ILink {
  type: string;
  url: string;
}

export interface IOffer {
  id: number;
  tokenId: number;
  offerPrice: number;
  creatorAddress: string;
  createdAt: string;
  updatedAt: string;
  amount: number;
  sellerRoyalty: number;
  status: number;
  version: number;
  contractAddress: string | null;
  offeringId: number;
  denom: string;
  quantity?: number;
}

export interface IOwnerStatus {
  id: number;
  tokenId: number;
  ownerId: number;
  ownerAddress: string;
  totalQuantity: number;
  availableQuantity: number;
  createdAt: string;
  updatedAt: string;
}

export interface IAttributes {
  isLiked: boolean;
  numberOfLikes: number;
}

export interface ICreator {
  id: number;
  tokenId: number;
  creatorAddress: string;
  creatorRoyalty: number;
  creatorType: string;
  createdAt: string;
  creator: IUser;
}

interface ILink {
  type: string;
  url: string;
}

export interface ICreators {
  creators: ICreator[];
}

export interface ITag {
  id: number;
  assetId: number;
  tagId: number;
  content: string;
  createdAt: string;
}

export enum STATUS_PROCESSING {
  Minting = 5,
  Minted = 6,
  MintFailed = 7,
  ViewSimilarity = 4,
  SimilarityChecking = 3,
  New = 1,
  Retry = 2,
  GenerateError = -1,
  GeneratePending = 0,
  Transfering = 8,
}

export interface IAssetProcessing {
  campaignId: number;
  campaign: {
    banner: string;
    name: string;
    rewardTitle: string;
    tag: string;
  };
  hide: boolean;
  id: number;
  midjourneyId: number;
  midjourneyPromptType: string | null;
  name?: string | null;
  description?: string | null;
  url: string;
  mimeType: string;
  type: string;
  status: STATUS_PROCESSING;
  message: string | null;
  creator: number;
  owner: number;
  modelId: number;
  tokenId: string | null;
  promptId: number;
  createdAt: string;
  updatedAt: string;
  asset: IAssetDetail;
  originalUrl: string;
  useCase: IUsecaseDetail;
  model: {
    id: number;
    name: string;
    description: string | null;
    thumbnail: string | null;
    thumbnailMimeType: string | null;
    picture: string | null;
    cover: string | null;
    apiSignature: string | null;
    apiContentType: string | null;
    status: number;
    isHeavyModel: number;
    isHeavySimilarity: number;
    providerId: number | null;
    updatedBy: number | null;
    categoryId: number | null;
    totalUsedTimes: number | null;
    threshold: number | null;
    createdAt: string;
    updatedAt: string;
    properties: any | null;
    creatorAddress: string | null;
  };
  creatorProvider: {
    id: number;
    username: string;
    picture: string | null;
    cover: string | null;
    status: number;
    publicAddress: string;
    description: string | null;
    job: string | null;
    tags: string[] | null;
    links: string[] | null;
    createdBy: number | null;
    updatedBy: number | null;
    role: number;
    totalNfts: number;
    isEmailVerified: boolean | null;
    isDiscordVerified: boolean | null;
    stripeId: string | null;
    createdAt: string;
    updatedAt: string;
    newNotificationsCount: number | null;
    nonce: string;
  };
}

export interface IProcessingDetail {
  id: number;
  midjourneyId: 428 | null;
  midjourneyPromptType: string | null;
  name: string | null;
  description: string | null;
  url: string;
  mimeType: string;
  type: string;
  status: number;
  message: string | null;
  creator: number;
  owner: number;
  modelId: number;
  tokenId: string | null;
  promptId: number;
  createdAt: string;
  updatedAt: string;
  originalUrl?: string | null;
  asset: IProcessingAsset;
  royalty: number | null;
  isNsfw: boolean;
  prompt: {
    id: number;
    createdAt: string;
    prompt: string;
    tokenId: number | null;
    updatedAt: string;
    displayPrompt: string;
  };
  modelConfig: string;
}

export interface IProcessingAsset {
  name: string;
  description: string;
  originalUrl: string;
  url: string;
  mimeType: string;
  creator: number;
  modelId: number;
  generatorRoyalty: number;
  prompt: string | null;
  picture: string | null;
  pictureMimeType: string | null;
  color: string | null;
  status: number;
  autoGeneratingPost: number;
  nftStatus: number | null;
  blockTime: string | null;
  blockHash: string | null;
  txHash: string | null;
  accountAddress: string | null;
  duration: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  collectionId: string | null;
  totalQuantity: number;
  availableQuantity: number;
  numberOfOwners: number;
  numberOfReactions: number;
  tokenContract: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  promptId: number | null;
  id: number;
  tags: ITag[];
}
export interface IProcessingUpdateResponse {
  processing_aseet: IProcessingDetail;
  asset: IProcessingAsset;
}

export interface TransactionHistory {
  txHash: string;
  height: number;
  timestamp: string;
  tokenId: number;
  sender: string;
  receiver: string;
  contract: string;
  event: string;
  price: string;
  denom: string;
  msg: {
    msg: string;
    amount: string;
    contract: string;
    attributes: Array<{
      key: string;
      value: string;
    }>;
    sendFunds: Array<{}>;
  };
  quantity: number;
  createdAt: string;
  updatedAt: string;
  seller: {
    id: number;
    username: string;
    picture: string;
    cover: string;
    status: number;
    publicAddress: string;
    description: string;
    job: string;
    tags: string;
    links: Array<{
      type: string;
      url: string;
    }>;
    createdBy: number;
    updatedBy: number;
    role: number;
    totalNfts: number;
    isEmailVerified: boolean;
    isDiscordVerified: boolean;
    stripeId: string;
    createdAt: string;
    updatedAt: string;
    newNotificationsCount: number;
    nonce: string;
  };
  buyer: {
    id: number;
    username: string;
    picture: string;
    cover: string;
    status: number;
    publicAddress: string;
    description: string;
    job: string;
    tags: string;
    links: Array<{
      type: string;
      url: string;
    }>;
    createdBy: number;
    updatedBy: number;
    role: number;
    totalNfts: number;
    isEmailVerified: boolean;
    isDiscordVerified: boolean;
    stripeId: string;
    createdAt: string;
    updatedAt: string;
    newNotificationsCount: number;
    nonce: string;
  } | null;
}

export enum PERIOD_ARTWORK_RANK {
  hour = 'H1',
  day = 'D1',
  week = 'D7',
  month = 'D30',
}

export interface AssetRankMarketplace {
  id: number;
  tokenId: number;
  oneHour: {
    AIRI?: number;
    USDT?: number;
  };
  oneHourVolume: number | null;
  oneHourChange: number | null;
  sixHour: {
    AIRI?: number;
    USDT?: number;
  };
  sixHourVolume: number | null;
  sixHourChange: number | null;
  oneDay: {
    AIRI?: number;
    USDT?: number;
  };
  oneDayVolume: number | null;
  oneDayChange: number | null;
  oneWeek: {
    AIRI?: number;
    USDT?: number;
  };
  oneWeekVolume: number | null;
  oneWeekChange: number | null;
  thirtyDay: {
    AIRI?: number;
    USDT?: number;
  };
  thirtyDayVolume: number | null;
  thirtyDayChange: number | null;
  allTime: {
    AIRI?: number;
    USDT?: number;
  };
  allVolume: number | null;
  createdAt: string;
  updatedAt: string;
  asset: {
    id: number;
    name: string;
    picture: string;
    pictureMimeType: string;
    offer: {
      id: number;
      offeringId: number;
      amount: number;
      denom: TokenDenom;
    };
  };
}

export interface AssetTrendingMarketplace {
  id: number;
  allTime: {
    AIRI?: number;
    USDT?: number;
  };
  allVolume: number | null;
  asset: {
    id: number;
    name: string;
    description: string;
    picture: string;
    pictureMimeType: string;
    url: string;
    mimeType: string;
    createdAt: string;
    offer: {
      id: number;
      offeringId: number;
      amount: number;
      denom: TokenDenom;
    };
    provider: {
      id: number;
      username: string;
      picture: string;
      publicAddress: string;
    };
  };
}
export interface OfferAsk {
  id: number;
  tokenId: number;
  offeringId: number;
  auctionId: number | null;
  sellerAddress: string;
  creatorAddress: string;
  bidderAddress: string | null;
  type: number;
  status: number;
  openPrice: number | null;
  buyoutPrice: number | null;
  currentPrice: number;
  auctionStepAmount: number | null;
  quantity: number;
  offerAt: string | null;
  closeAt: string | null;
  createdAt: string;
  updatedAt: string;
  contractAddress: string;
  denom: string;
  seller: OfferSeller;
}

interface OfferSeller {
  id: number;
  username: string;
  picture: string;
  cover: string;
  status: number;
  publicAddress: string;
  description: string | null;
  job: string | null;
  tags: string[] | null;
  links: string[] | null;
  createdBy: number | null;
  updatedBy: number;
  role: number;
  totalNfts: number;
  isEmailVerified: boolean;
  isDiscordVerified: boolean;
  stripeId: string;
  createdAt: string;
  updatedAt: string;
  newNotificationsCount: number;
  nonce: string;
}

export interface AssetOwner {
  id: number;
  tokenId: number;
  ownerId: number;
  ownerAddress: string;
  totalQuantity: number;
  availableQuantity: number;
  createdAt: string;
  updatedAt: string;
  provider: Provider;
}
